import React, { useState, useEffect } from "react";
import {
 createVocabularyGroup,
 updateVocabularyGroup,
 fetchVocabularyGroupById,
 fetchVocabulariesForDropdown,
} from "./adminVocabularyGroupService";
import { useToast } from "../../../common/toast/ToastContext";

const VocabularyGroupForm = ({ group, onSuccess, onCancel }) => {
 const { showToast } = useToast();
 
 // Helper function to capitalize part of speech
 const capitalizePartOfSpeech = (pos) => {
 if (!pos) return pos;
 return pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
 };
 const [formData, setFormData] = useState({
 name: "",
 description: "",
 vocabularyIds: [],
 });
 const [vocabularies, setVocabularies] = useState([]);
 const [selectedVocabularies, setSelectedVocabularies] = useState([]);
 const [vocabularySearch, setVocabularySearch] = useState("");
 const [showVocabularyDropdown, setShowVocabularyDropdown] = useState(false);
 const [loading, setLoading] = useState(false);
 const [loadingVocabularies, setLoadingVocabularies] = useState(false);

 const isEdit = !!group;

 useEffect(() => {
 if (isEdit) {
 loadGroupData();
 }
 loadVocabularies();
 }, []);

 const loadGroupData = async () => {
 const result = await fetchVocabularyGroupById(group.id);
 if (result.status === 200) {
 const data = result.data;
 setFormData({
 name: data.name || "",
 description: data.description || "",
 vocabularyIds: data.vocabularies?.map(v => v.id) || [],
 });
 setSelectedVocabularies(data.vocabularies || []);
 }
 };

 const loadVocabularies = async (search = "") => {
 setLoadingVocabularies(true);
 const params = { page: 0, size: 50 };
 if (search.trim()) params.search = search.trim();

 const result = await fetchVocabulariesForDropdown(params);
 if (result.status === 200) {
 setVocabularies(result.data.content || []);
 }
 setLoadingVocabularies(false);
 };

 const handleInputChange = (e) => {
 const { name, value } = e.target;
 setFormData(prev => ({ ...prev, [name]: value }));
 };

 const handleVocabularySearch = (e) => {
 const value = e.target.value;
 setVocabularySearch(value);
 loadVocabularies(value);
 };

 const handleVocabularySelect = (vocabulary) => {
 if (!formData.vocabularyIds.includes(vocabulary.id)) {
 setFormData(prev => ({
 ...prev,
 vocabularyIds: [...prev.vocabularyIds, vocabulary.id]
 }));
 setSelectedVocabularies(prev => [...prev, vocabulary]);
 }
 setVocabularySearch("");
 setShowVocabularyDropdown(false);
 };

 const handleVocabularyRemove = (vocabularyId) => {
 setFormData(prev => ({
 ...prev,
 vocabularyIds: prev.vocabularyIds.filter(id => id !== vocabularyId)
 }));
 setSelectedVocabularies(prev => prev.filter(v => v.id !== vocabularyId));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 
 if (!formData.name.trim()) {
 showToast("Name is required", "error");
 return;
 }
 
 if (formData.vocabularyIds.length === 0) {
 showToast("At least one vocabulary is required", "error");
 return;
 }

 setLoading(true);
 
 const payload = {
 name: formData.name.trim(),
 description: formData.description.trim() || null,
 vocabularyIds: formData.vocabularyIds,
 };

 const result = isEdit
 ? await updateVocabularyGroup(group.id, payload)
 : await createVocabularyGroup(payload);

 if (result.status === 200 || result.status === 201) {
 showToast(result.message, "success");
 onSuccess();
 } else {
 showToast(result.message, "error");
 }
 
 setLoading(false);
 };

 const filteredVocabularies = vocabularies.filter(v => 
 !formData.vocabularyIds.includes(v.id) &&
 v.word.toLowerCase().includes(vocabularySearch.toLowerCase())
 );

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
 <div className="p-6">
 <h2 className="text-xl font-semibold mb-4">
 {isEdit ? "Edit Vocabulary Group" : "Create Vocabulary Group"}
 </h2>

 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Name */}
 <div>
 <label className="block text-sm font-medium mb-1">
 Name <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 name="name"
 value={formData.name}
 onChange={handleInputChange}
 className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="Enter group name"
 required
 />
 </div>

 {/* Description */}
 <div>
 <label className="block text-sm font-medium mb-1">Description</label>
 <textarea
 name="description"
 value={formData.description}
 onChange={handleInputChange}
 rows="3"
 className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="Enter description (optional)"
 />
 </div>

 {/* Vocabulary Selector */}
 <div>
 <label className="block text-sm font-medium mb-1">
 Vocabularies <span className="text-red-500">*</span>
 </label>
 
 {/* Selected Vocabularies */}
 {selectedVocabularies.length > 0 && (
 <div className="mb-2 flex flex-wrap gap-2">
 {selectedVocabularies.map(vocab => (
 <span
 key={vocab.id}
 className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
 >
 {vocab.word} ({vocab.difficulty})
 <button
 type="button"
 onClick={() => handleVocabularyRemove(vocab.id)}
 className="ml-1 text-blue-600 hover:text-blue-800"
 >
 ×
 </button>
 </span>
 ))}
 </div>
 )}

 {/* Search Input */}
 <div className="relative">
 <input
 type="text"
 value={vocabularySearch}
 onChange={handleVocabularySearch}
 onFocus={() => setShowVocabularyDropdown(true)}
 className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="Search and select vocabularies..."
 />

 {/* Dropdown */}
 {showVocabularyDropdown && (
 <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
 {loadingVocabularies ? (
 <div className="p-3 text-center text-gray-500">Loading...</div>
 ) : filteredVocabularies.length === 0 ? (
 <div className="p-3 text-center text-gray-500">
 {vocabularySearch ? "No vocabularies found" : "Start typing to search"}
 </div>
 ) : (
 filteredVocabularies.map(vocab => (
 <button
 key={vocab.id}
 type="button"
 onClick={() => handleVocabularySelect(vocab)}
 className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
 >
 <div className="font-medium">{vocab.word}</div>
 <div className="text-sm text-gray-600 flex items-center gap-1 flex-wrap">
 <span>{vocab.difficulty}</span>
 {Array.isArray(vocab.partOfSpeech) 
 ? vocab.partOfSpeech.map((pos, index) => (
 <span 
 key={index}
 className="inline-block bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs"
 >
 {capitalizePartOfSpeech(pos)}
 </span>
 ))
 : vocab.partOfSpeech && (
 <span className="inline-block bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">
 {capitalizePartOfSpeech(vocab.partOfSpeech)}
 </span>
 )
 }
 </div>
 </button>
 ))
 )}
 </div>
 )}
 </div>
 </div>

 {/* Actions */}
 <div className="flex justify-end space-x-3 pt-4">
 <button
 type="button"
 onClick={onCancel}
 className="px-4 py-2 border rounded hover:bg-gray-50"
 disabled={loading}
 >
 Cancel
 </button>
 <button
 type="submit"
 disabled={loading}
 className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
 >
 {loading ? "Saving..." : isEdit ? "Update" : "Create"}
 </button>
 </div>
 </form>
 </div>
 </div>

 {/* Click outside to close dropdown */}
 {showVocabularyDropdown && (
 <div
 className="fixed inset-0 z-5"
 onClick={() => setShowVocabularyDropdown(false)}
 />
 )}
 </div>
 );
};

export default VocabularyGroupForm;